import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './EditScenario.module.css';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import { TemplateType } from '../../api/types/scenario';
import { useDevice } from '../../context/DeviceContext';
import { api } from '../../api';
import ShareScenario from './ShareScenario';
import EditScenarioDetails from './EditScenarioDetails';
import EditScenarioCharacters from './EditScenarioCharacters';
import EditScenarioMessages from './EditScenarioMessages';
import EditScenarioWorld from './EditScenarioWorld';
import EditScenarioSidebar from './EditScenarioSidebar';
import EditScenarioNavBar from './EditScenarioNavBar';

interface EditScenarioProps {
  definition: ScenarioDefinitionType;
  onDefinitionUpdate?: (updatedDefinition: ScenarioDefinitionType) => void;
}

function EditScenario({ definition: initialDefinition, onDefinitionUpdate }: EditScenarioProps): JSX.Element {
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [activeSection, setActiveSection] = useState<'details' | 'characters' | 'messages' | 'world'>('details');
  const [definition, setDefinition] = useState<ScenarioDefinitionType>(initialDefinition);

  useEffect(() => {
    const loadDefinition = async () => {
      try {
        const freshDefinition = await api.scenarioDefinitions.get(scenarioId as string);
        setDefinition(freshDefinition);
        if (onDefinitionUpdate) {
          onDefinitionUpdate(freshDefinition);
        }
      } catch (err) {
        console.error('Failed to load scenario definition:', err);
      }
    };

    loadDefinition();
  }, [scenarioId, onDefinitionUpdate]);

  const handleClose = () => {
    navigate(`/scenarios/${definition.id}`);
  };

  const handleSidebarNavigation = (section: 'details' | 'characters' | 'messages' | 'world') => {
    setActiveSection(section);
  };

  const handleDefinitionUpdate = useCallback((updatedDefinition: ScenarioDefinitionType) => {
    setDefinition(updatedDefinition);
    if (onDefinitionUpdate) {
      onDefinitionUpdate(updatedDefinition);
    }
  }, [onDefinitionUpdate]);

  const handleEditMessage = useCallback(async (messageId: string, updates: {
    templateType?: TemplateType;
    characterId?: string;
    content?: string;
  }) => {
    try {
      const updatedDefinition = await definition.editMessage(messageId, updates);
      setDefinition(updatedDefinition);
      if (onDefinitionUpdate) {
        onDefinitionUpdate(updatedDefinition);
      }
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  }, [definition, onDefinitionUpdate]);

  const handleAddMessage = useCallback(async (templateType: TemplateType, characterId: string, content: string) => {
    try {
      const updatedDefinition = await definition.addMessage(templateType, characterId, content);
      setDefinition(updatedDefinition);
      if (onDefinitionUpdate) {
        onDefinitionUpdate(updatedDefinition);
      }
    } catch (error) {
      console.error('Failed to add message:', error);
    }
  }, [definition, onDefinitionUpdate]);

  const handleRemoveMessage = useCallback(async (messageId: string) => {
    try {
      const updatedDefinition = await definition.removeMessage(messageId);
      setDefinition(updatedDefinition);
      if (onDefinitionUpdate) {
        onDefinitionUpdate(updatedDefinition);
      }
    } catch (error) {
      console.error('Failed to remove message:', error);
    }
  }, [definition, onDefinitionUpdate]);

  const renderContent = () => {
    switch (activeSection) {
      case 'characters':
        return <EditScenarioCharacters definition={definition} setDefinition={setDefinition} />;
      case 'messages':
        return <EditScenarioMessages 
          definition={definition} 
          onEditMessage={handleEditMessage}
          onAddMessage={handleAddMessage}
          onDeleteMessage={handleRemoveMessage}
          setDefinition={setDefinition}
        />;
      case 'world':
        return <EditScenarioWorld definition={definition} onUpdate={handleDefinitionUpdate} />;
      case 'details':
      default:
        return <EditScenarioDetails definition={definition} onUpdate={handleDefinitionUpdate} />;
    }
  };

  return (
    <div className={styles.verticalContainer}>
      {!isMobile && (
        <EditScenarioSidebar 
          onNavigate={handleSidebarNavigation} 
          onClose={handleClose}
        />
      )}
      
      <div className={styles.mainContent}>
        {isMobile && (
          <button className={styles.backButton} onClick={handleClose}>
            ‹
          </button>
        )}
        {!isMobile && activeSection === 'details' && <ShareScenario scenarioDefinition={definition} />}
        {renderContent()}
      </div>

      {isMobile && (
        <EditScenarioNavBar 
          activeSection={activeSection}
          onNavigate={handleSidebarNavigation}
        />
      )}
    </div>
  );
}

export default EditScenario; 